import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getEnvironmentVariables, isEmpty, isFull } from "../utils";
import { isValidJson } from "../jsonUtils";
import { Auth, Marketplace } from "../constants";

export const decodeJwt = (jwt) => {
  try {
    return jwtDecode(jwt);
  } catch (ex) {
    return undefined;
  }
};

export const isJwtValid = (jwt) => {
  let decodedJwt = decodeJwt(jwt);
  if (isEmpty(decodedJwt)) {
    return false;
  }

  if (
    isFull(decodedJwt[Auth.Claims.Email]) &&
    isFull(decodedJwt[Auth.Claims.UserId])) {
    return true;
  }

  return false;
};

export const isJwtMultiFactorAuthenticated = (jwt) => {
  let decodedJwt = decodeJwt(jwt);
  if (isEmpty(decodedJwt)) {
    return false;
  }

  if (
    isFull(decodedJwt[Auth.Claims.Email]) &&
    isFull(decodedJwt[Auth.Claims.UserId]) &&
    decodedJwt[Auth.Claims.AuthenticationMethod] === Auth.AuthenticationMethods.TwoFactorAuth
  ) {
    return true;
  }

  return false;
};

/**
 * The result of a Google or Microsoft sign-in ({ user, valid }), which Identity hands to the
 * Portal in the hand-off cookie for five minutes (ExternalLoginCallback in
 * Api/Controllers/AccountController.cs of pellerex.ecosystem.core.identityapi). Identity sets that
 * cookie for its environment's domain. Production's domain, pellerex.com, includes staging's
 * stg.pellerex.com, so production's cookie also reaches staging's pages. The result is used only
 * when its access token names the issuer this environment's Identity writes
 * (REACT_APP_IDENTITY_TOKEN_ISSUER); otherwise there is none. This is not a security check: the
 * token is read without its signature, and the services check every token they are sent.
 */
export const readExternalSignInForThisEnvironment = () => {
  // isValidJson leaves it to its caller whether blank text counts: a blank cookie holds no sign-in.
  const handOff = Cookies.get(Auth.TokenHolderCookieName);
  if (isEmpty(handOff?.trim()) || !isValidJson(handOff)) {
    return undefined;
  }

  const signIn = JSON.parse(handOff);
  const tokenIssuer = decodeJwt(signIn?.user?.accessToken)?.[Auth.Claims.Issuer];
  if (isEmpty(tokenIssuer) || tokenIssuer !== getEnvironmentVariables().REACT_APP_IDENTITY_TOKEN_ISSUER) {
    return undefined;
  }

  return signIn;
};

export const getRoleLevel = (role) => {
  switch (role) {
    case Auth.Roles.OrganisationContributor.Code:
    case Auth.Roles.OrganisationOwner.Code:
    case Auth.Roles.OrganisationReader.Code:
      return Auth.RoleLevel.Organisation.Code;
    case Auth.Roles.AppSpaceContributor.Code:
    case Auth.Roles.AppSpaceOwner.Code:
    case Auth.Roles.AppSpaceReader.Code:
      return Auth.RoleLevel.AppSpace.Code;
    case Auth.Roles.TenantContributor.Code:
    case Auth.Roles.TenantOwner.Code:
    case Auth.Roles.TenantReader.Code:
      return Auth.RoleLevel.Tenant.Code;
    case Auth.Roles.AppContributor.Code:
    case Auth.Roles.AppOwner.Code:
    case Auth.Roles.AppReader.Code:
      return Auth.RoleLevel.App.Code;
    case Auth.Roles.SystemOwner.Code:
      return Auth.RoleLevel.Universe.Code;
  }
};

export const getRoleName = (role) => {
  switch (role.toUpperCase()) {
    case Auth.Roles.OrganisationContributor.Code:
      return Auth.Roles.OrganisationContributor.Name;
    case Auth.Roles.OrganisationOwner.Code:
      return Auth.Roles.OrganisationOwner.Name;
    case Auth.Roles.OrganisationReader.Code:
      return Auth.Roles.OrganisationReader.Name;
    case Auth.Roles.AppSpaceContributor.Code:
      return Auth.Roles.AppSpaceContributor.Name;
    case Auth.Roles.AppSpaceOwner.Code:
      return Auth.Roles.AppSpaceOwner.Name;
    case Auth.Roles.AppSpaceReader.Code:
      return Auth.Roles.AppSpaceReader.Name;
    case Auth.Roles.TenantContributor.Code:
      return Auth.Roles.TenantContributor.Name;
    case Auth.Roles.TenantOwner.Code:
      return Auth.Roles.TenantOwner.Name;
    case Auth.Roles.TenantReader.Code:
      return Auth.Roles.TenantReader.Name;
    case Auth.Roles.AppContributor.Code:
      return Auth.Roles.AppContributor.Name;
    case Auth.Roles.AppOwner.Code:
      return Auth.Roles.AppOwner.Name;
    case Auth.Roles.AppReader.Code:
      return Auth.Roles.AppReader.Name;
    case Auth.Roles.SystemOwner.Code:
      return Auth.Roles.SystemOwner.Name;
  }
};

export const getRoleHierarchy = ({ resourceType, role }) => {
  let roleLevel = getRoleLevel(role);

  switch (roleLevel) {
    case Auth.RoleLevel.App.Code: {
      switch (resourceType) {
        case Marketplace.ResourceTypes.App:
          return Marketplace.AccessSource.ThroughDirectAssignment.Code;

        default:
          return Marketplace.AccessSource.InheritedPermission.Code;
      }
    }

    case Auth.RoleLevel.AppSpace.Code: {
      switch (resourceType) {
        case Marketplace.ResourceTypes.AppSpace:
          return Marketplace.AccessSource.ThroughDirectAssignment.Code;

        default:
          return Marketplace.AccessSource.InheritedPermission.Code;
      }
    }

    case Auth.RoleLevel.Organisation.Code: {
      switch (resourceType) {
        case Marketplace.ResourceTypes.Organisation:
          return Marketplace.AccessSource.ThroughDirectAssignment.Code;

        default:
          return Marketplace.AccessSource.InheritedPermission.Code;
      }
    }

    case Auth.RoleLevel.Tenant.Code: {
      switch (resourceType) {
        case Marketplace.ResourceTypes.Tenant:
          return Marketplace.AccessSource.ThroughDirectAssignment.Code;

        default:
          return Marketplace.AccessSource.InheritedPermission.Code;
      }
    }
  }
  return Marketplace.AccessSource.InheritedPermission.Code;
};

//a function to return full role name by receing a resource type and role
export const getRoleNormalisedName = ({ resourceType, role }) => {
  return `${resourceType.toUpperCase()}-${role.toUpperCase()}`;
};
